import type { SettingsListPreferences } from "./settingsListTypes";
import type { PersistedSorter } from "./settingsListTypes";
export type { PersistedSorter } from "./settingsListTypes";
import type { ListColumn } from "../list/listColumn";
import React from "react";
import { ANONYMOUS_ACCOUNT_ID, VALUE_TYPE_OPTION } from "./settingsListConstants";


export type AntdSortOrder = "ascend" | "descend" | null | undefined;

/** Vergleichsfunktion einer Spalte; die Zeilenform ist an der Aufrufstelle nicht bekannt. */
export type ColumnSorter = (a: unknown, b: unknown) => number;

/**
 * Sortierschlüssel einer Spalte, die keinen einfachen `dataIndex` hat — also überall dort, wo der
 * angezeigte Wert erst im `render` entsteht (zusammengesetzte Texte, Tags, abgeleitete Zustände).
 * Zurückgegeben wird der Wert, nach dem der Nutzer sortieren würde: Zahl, Datum als ISO-String,
 * Boolean oder Anzeigetext. Damit greift derselbe Vergleich wie bei `dataIndex`-Spalten; eine
 * eigene `sorter`-Funktion bleibt weiterhin möglich und hat Vorrang.
 */
export type ColumnSortValue<T = never> = (record: T) => unknown;

/** Spaltenerweiterung: Sortierung für Spalten ohne `dataIndex`, ohne handgeschriebenen Comparator. */
export type SortableColumnExtension<T = never> = {
  sortValue?: ColumnSortValue<T>;
};

function storageKey(accountId: string, tableKey: string) {
  return `proTable.sort.${accountId}.${tableKey}`;
}

function safeJsonParse<T>(s: string | null): T | null {
  if (!s) return null;
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

/** Form, in der antd den Sortierzustand meldet (einzeln oder als Liste bei Mehrfachsortierung). */
export type AntdSorterInput = { field?: unknown; columnKey?: unknown; order?: AntdSortOrder };

function normalizeSorterFromOnChange(sorter: AntdSorterInput | AntdSorterInput[] | null | undefined): PersistedSorter {
  if (!sorter) return null;
  const s = Array.isArray(sorter) ? sorter[0] : sorter;
  const field = s?.field ?? s?.columnKey;
  const order = (s?.order ?? null) as AntdSortOrder;
  // Order=null kommt vom dritten Klick: die manuelle Sortierung endet, die Systemsortierung
  // (`defaultSortOrder`, ohne Pfeil) greift wieder (Ronny, 15.10.2026: eine Struktur für alle Listen).
  if (!field || (order !== "ascend" && order !== "descend")) return null;
  return { field: String(field), order };
}

function isActiveSort(s: PersistedSorter): s is { field: string; order: Exclude<AntdSortOrder, undefined> } {
  if (!s || !("field" in s)) return false;
  return typeof s.field === "string" && (s.order === "ascend" || s.order === "descend");
}


// ProTable request() receives sort as a map: { [field]: "ascend" | "descend" }
function normalizeSorterFromRequest(sort: Record<string, AntdSortOrder> | undefined): PersistedSorter {
  if (!sort) return null;
  const entries = Object.entries(sort).filter(([, v]) => v === "ascend" || v === "descend");
  if (!entries.length) return null;
  const [field, order] = entries[0];
  return { field: String(field), order: order as Exclude<AntdSortOrder, undefined> };
}

function getByPath(obj: unknown, path: string): unknown {
  if (!obj) return undefined;
  const parts = path.split(".");
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  // numbers
  if (typeof a === "number" && typeof b === "number") return a - b;
  // booleans
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);
  // dates
  const da = typeof a === "string" ? Date.parse(a) : NaN;
  const db = typeof b === "string" ? Date.parse(b) : NaN;
  if (Number.isFinite(da) && Number.isFinite(db)) return da - db;

  return String(a).localeCompare(String(b), "de", { sensitivity: "base" });
}

function columnDataIndex(column: { dataIndex?: unknown }): string {
  return Array.isArray(column.dataIndex) ? column.dataIndex.join(".") : String(column.dataIndex || "");
}

/** Sichtbarer Text eines gerenderten Zellinhalts (Text, Zahlen, Kinder von Elementen). */
function renderedNodeText(node: unknown): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(renderedNodeText).filter(Boolean).join(" ");
  if (React.isValidElement(node)) return renderedNodeText((node.props as { children?: unknown }).children);
  return "";
}

/**
 * Letzte Stufe für Fachspalten ohne `sortValue` und ohne `dataIndex`: verglichen wird der angezeigte
 * Text aus `render`. Jede Fachspalte ist damit sortierbar (Ronny, 18.10.2026: keine Ausnahmen).
 */
function renderedTextSorter<T>(column: ListColumn<T>): (a: T, b: T) => number {
  const key = column.key != null ? String(column.key) : "";
  const render = column.render as ((value: unknown, record: T, index: number) => unknown) | undefined;
  const cache = new WeakMap<object, unknown>();
  const valueOf = (record: T): unknown => {
    if (record == null || typeof record !== "object") return record;
    if (cache.has(record)) return cache.get(record);
    const raw = key ? getByPath(record, key) : undefined;
    let value: unknown = raw;
    if (render) {
      try {
        const text = renderedNodeText(render(raw, record, 0)).trim();
        if (text) value = text;
      } catch {
        // Zelle braucht Kontext außerhalb der Liste: der Rohwert bleibt der Sortierschlüssel.
      }
    }
    if (value != null && typeof value === "object") value = undefined;
    cache.set(record, value);
    return value;
  };
  return (a: T, b: T) => compareValues(valueOf(a), valueOf(b));
}

/**
 * Leitet den Vergleich einer Spalte aus `sorter`, `sortValue`, `dataIndex` bzw. dem angezeigten Text ab —
 * dieselbe Regel, die `makeColumnsSortable` anwendet, aber für Tabellen ohne Sortier-Persistenz (z. B. die
 * aufgeklappten Detailzeilen einer Gruppenliste). `null` nur für Aktionsspalten.
 */
export function deriveColumnSorter<T>(
  column: ListColumn<T> & SortableColumnExtension<T>
): ((a: T, b: T) => number) | null {
  if (column.valueType === VALUE_TYPE_OPTION) return null;
  if (typeof column.sorter === "function") return column.sorter as (a: T, b: T) => number;
  if (column.sorter != null && typeof column.sorter === "object" && typeof column.sorter.compare === "function") return column.sorter.compare;
  const sortValue = column.sortValue;
  if (typeof sortValue === "function") return (a: T, b: T) => compareValues(sortValue(a), sortValue(b));
  const dataIndex = columnDataIndex(column);
  if (dataIndex) return (a: T, b: T) => compareValues(getByPath(a, dataIndex), getByPath(b, dataIndex));
  return renderedTextSorter(column);
}

/**
 * Ob eine Spalte in einer serverseitigen Liste sortierbar angemeldet wird. Die empfangene Seite
 * wird nie lokal umsortiert — deshalb nur `sorter: true`, nie ein Comparator. Jede Fachspalte wird
 * angemeldet, mit `dataIndex` bzw. ohne ihn mit ihrem `key` als Sortierfeld; nur Aktionsspalten
 * (`option`) nicht (Ronny, 18.10.2026: keine Ausnahmen). Der Endpunkt muss jedes Feld kennen.
 */
export function columnAnnouncesServerSort<T>(
  column: ListColumn<T> & SortableColumnExtension<T>
): boolean {
  return column.valueType !== VALUE_TYPE_OPTION;
}

export function applyPersistedSorter<T>(
  rows: T[],
  sorter: PersistedSorter,
  /** Optionaler Spalten-eigener Sorter (Aufsteigend). Wird verwendet, wenn die Spalte
   *  keinen plain-`dataIndex`-Pfad hat (z. B. abgeleitete Status-/Summary-Spalten). */
  columnSorter?: ColumnSorter | null
): T[] {
  if (!isActiveSort(sorter)) return rows;
  const { order } = sorter;
  const dir = order === "ascend" ? 1 : -1;
  if (columnSorter) {
    return [...rows].sort((ra, rb) => dir * columnSorter(ra, rb));
  }
  const { field } = sorter;
  return [...rows].sort((ra, rb) => dir * compareValues(getByPath(ra, field), getByPath(rb, field)));
}

export function useProTableSortPersistence(accountIdRaw: string | undefined | null, tableKey: string, ui: SettingsListPreferences) {
  
  const accountId = (accountIdRaw || ANONYMOUS_ACCOUNT_ID).trim() || ANONYMOUS_ACCOUNT_ID;
  const serverSorter = ui.prefs.proTableSort?.[tableKey] ?? null;

  const [persisted, setPersisted] = React.useState<PersistedSorter>(() => {
    if (typeof window === "undefined") return null;
    return safeJsonParse<PersistedSorter>(window.localStorage.getItem(storageKey(accountId, tableKey)));
  });

  // Prefer server value when available.
  React.useEffect(() => {
    if (accountId === ANONYMOUS_ACCOUNT_ID) return;
    if (serverSorter == null) return;
    setPersisted(serverSorter);
  }, [accountId, serverSorter]);

  // Lazy-migrate local -> server (per tableKey) once the provider is ready.
  React.useEffect(() => {
    if (accountId === ANONYMOUS_ACCOUNT_ID) return;
    if (!ui.isReady) return;
    const hasOnServer = ui.prefs.proTableSort?.[tableKey] != null;
    if (hasOnServer) return;
    if (!persisted) return;
    ui.patchPrefs({ proTableSort: { [tableKey]: persisted } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, ui.isReady, tableKey]);

  const save = React.useCallback(
    (next: PersistedSorter) => {
      setPersisted(next);
      if (typeof window === "undefined") return;
      // null = Initial (key entfernen, fallback darf greifen).
      // Manuelle Sortierung aufgehoben = null; die Systemsortierung greift wieder.
      if (next == null) window.localStorage.removeItem(storageKey(accountId, tableKey));
      else window.localStorage.setItem(storageKey(accountId, tableKey), JSON.stringify(next));

      if (accountId !== ANONYMOUS_ACCOUNT_ID) {
        if (next == null) {
          ui.patchPrefs({ proTableSort: { [tableKey]: null } as Record<string, PersistedSorter | null> });
        } else {
          ui.patchPrefs({ proTableSort: { [tableKey]: next } });
        }
      }
    },
    [accountId, tableKey, ui]
  );

  const onTableChange = React.useCallback(
    (_pagination: unknown, _filters: unknown, sorter: AntdSorterInput | AntdSorterInput[] | null | undefined) => {
      // 3-state sort: on "unsorted", AntD sends order undefined
      const next = normalizeSorterFromOnChange(sorter);
      save(next);
    },
    [save]
  );

  // Cache fuer den aktuellen defaultSortOrder-Fallback aus den Spalten — befuellt von
  // makeColumnsSortable, gelesen von sortRows beim ersten Daten-Load (wenn weder requestSort noch
  // persisted vorliegen). Damit greift defaultSortOrder auch in der server-/client-side request-Pipeline.
  const fallbackRef = React.useRef<{ field: string; order: Exclude<AntdSortOrder, undefined> } | null>(null);
  /** Map <field, column.sorter>: damit sortRows fuer Spalten ohne plain-dataIndex (nur `key`,
   *  z. B. abgeleitete Status/Summary-Spalten) die Spalten-eigene Sorter-Funktion verwenden kann. */
  const columnSortersRef = React.useRef<Map<string, ColumnSorter>>(new Map());

  const sortRows = React.useCallback(
    <T,>(rows: T[], requestSort?: Record<string, AntdSortOrder>) => {
      const fromRequest = normalizeSorterFromRequest(requestSort);
      if (fromRequest) {
        const fn = isActiveSort(fromRequest) ? columnSortersRef.current.get(fromRequest.field) ?? null : null;
        return applyPersistedSorter(rows, fromRequest, fn);
      }
      if (isActiveSort(persisted)) {
        const fn = columnSortersRef.current.get(persisted.field) ?? null;
        return applyPersistedSorter(rows, persisted, fn);
      }
      if (fallbackRef.current) {
        const fn = columnSortersRef.current.get(fallbackRef.current.field) ?? null;
        return applyPersistedSorter(rows, fallbackRef.current, fn);
      }
      return rows;
    },
    [persisted]
  );

  const makeColumnsSortable = React.useCallback(
    <T,>(columns: ListColumn<T>[], serverSide = false) => {
      const active = isActiveSort(persisted) ? persisted : null;
      const activeField = active?.field;
      const activeOrder = active?.order;
      // Ohne manuelle Sortierung greift still der defaultSortOrder einer Spalte (Systemsortierung).
      // Ein früher gespeichertes {cleared:true} zählt wie keine manuelle Sortierung.
      let fallbackField: string | null = null;
      let fallbackOrder: AntdSortOrder = null;
      if (!activeField) {
        for (const c of columns || []) {
          if (c?.defaultSortOrder === "ascend" || c?.defaultSortOrder === "descend") {
            fallbackField = Array.isArray(c.dataIndex) ? c.dataIndex.join(".") : String(c.dataIndex || c.key || "");
            fallbackOrder = c.defaultSortOrder;
            break;
          }
        }
      }
      // Fuer sortRows persistieren — damit der request-Pfad ohne explizite Sortierung den Fallback nutzt.
      fallbackRef.current = (fallbackField && (fallbackOrder === "ascend" || fallbackOrder === "descend"))
        ? { field: fallbackField, order: fallbackOrder }
        : null;
      // Map fuer Spalten-eigene Sorter neu aufbauen.
      const sortersByField = new Map<string, ColumnSorter>();
      const result = (columns || []).map((c) => {
        const isOption = c?.valueType === VALUE_TYPE_OPTION;
        if (isOption) return c;
        const dataIndex = columnDataIndex(c);
        const columnKey = c?.key != null ? String(c.key) : "";
        // Antd liefert beim Klick `sorter.field ?? sorter.columnKey` — beides muss matchen koennen.
        const matchKey = dataIndex || columnKey;
        const next: ListColumn<T> = { ...c };
        if (serverSide) {
          // Fachspalten selbst anmelden. Einen lokalen Comparator zu loeschen, ohne `true`
          // zu setzen, macht die Spalte still unsortierbar — das war der Fehler in den Leads.
          next.sorter = true;
        } else {
          // Lokal sortierte Listen: jede Fachspalte ist sortierbar, ein Opt-out (`sorter: false`)
          // wird nicht mehr beachtet (Ronny, 18.10.2026). Eigener Vergleich > sortValue > dataIndex > Anzeigetext.
          const { sorter: ownSorter, ...rest } = c;
          next.sorter = deriveColumnSorter<T>(
            (typeof ownSorter === "function" || (ownSorter != null && typeof ownSorter === "object") ? c : rest) as ListColumn<T> & SortableColumnExtension<T>
          ) ?? undefined;
        }
        if (next.sorter != null && next.showSorterTooltip === undefined) {
          next.showSorterTooltip = false;
        }
        // Cache nur eine echte Sorter-Funktion fuer den Re-Sort im request-Pfad.
        if (matchKey && typeof next.sorter === "function") {
          sortersByField.set(matchKey, next.sorter as ColumnSorter);
        }
        // Nur die manuelle Sortierung zeigt einen Pfeil; die Systemsortierung bleibt unmarkiert.
        if (matchKey && activeField && matchKey === activeField) {
          next.sortOrder = activeOrder;
        } else if (matchKey) {
          next.sortOrder = null;
        }
        return next;
      });
      columnSortersRef.current = sortersByField;
      return result;
    },
    [persisted]
  );

  const getRequestSort = React.useCallback((): Record<string, "ascend" | "descend"> | undefined => {
    if (isActiveSort(persisted)) {
      return { [persisted.field]: persisted.order as "ascend" | "descend" };
    }
    if (fallbackRef.current) {
      return { [fallbackRef.current.field]: fallbackRef.current.order as "ascend" | "descend" };
    }
    return undefined;
  }, [persisted]);

  return React.useMemo(
    () => ({ persisted, onTableChange, sortRows, makeColumnsSortable, getRequestSort }),
    [persisted, onTableChange, sortRows, makeColumnsSortable, getRequestSort]
  );
}


/**
 * Die aktive Sortierung aus dem `sort`-Argument einer ProTable-`request`-Funktion. Serverseitige
 * Listen brauchen genau diese Angabe, um sie an ihren Endpunkt weiterzureichen; die Erkennung lag
 * vorher in jeder Liste einzeln kopiert. Die Namen der Query-Parameter bleiben bei der Aufrufstelle,
 * weil jeder Endpunkt seinen eigenen Vertrag hat (`sortBy`/`sortOrder`, `sort=feld:richtung`).
 */
export function getActiveRequestSort(
  sort: Record<string, AntdSortOrder> | undefined
): { field: string; direction: "asc" | "desc"; order: "ascend" | "descend" } | null {
  const entry = Object.entries(sort ?? {}).find(([, order]) => order === "ascend" || order === "descend");
  if (!entry) return null;
  const order = entry[1] as "ascend" | "descend";
  return { field: entry[0], direction: order === "ascend" ? "asc" : "desc", order };
}
